<template>
  <v-card class="regression-card" elevation="2" rounded="lg">
    <v-toolbar flat>
      <v-toolbar-title class="text-h6">
        <v-icon class="mr-2" color="primary">mdi-chart-scatter-plot</v-icon>
        Regressionsanalyse
      </v-toolbar-title>
    </v-toolbar>

    <v-card-text>
      <div class="text-caption text-medium-emphasis mb-3">
        Trend über die Jahre (unabhängige Variable: Jahr) mit Bestimmtheitsmaß R² und
        Residuenanalyse nach DIN EN 17669 Anhang A. Umschaltbar zwischen Verbrauch
        [kWh] und Emissionen [t CO₂e].
      </div>

      <div class="d-flex flex-wrap align-center ga-4 mb-3">
        <v-select
          v-model="selectedKey"
          :items="seriesOptions"
          item-title="label"
          item-value="key"
          label="Energieträger"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 320px"
        />

        <!--
          Verbrauch oder Emissionen. Je Träger ist die CO₂-Reihe nur skaliert;
          bei der Summe ändert sich die Form, weil die Träger verschieden
          schwer wiegen.
        -->
        <v-btn-toggle v-model="metric" mandatory density="compact" variant="outlined">
          <v-btn value="consumption" size="small">
            <v-icon start size="18">mdi-lightning-bolt</v-icon>
            Verbrauch
          </v-btn>
          <v-btn value="co2" size="small">
            <v-icon start size="18">mdi-molecule-co2</v-icon>
            CO₂e
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-alert
        v-if="incompleteSeriesLabels.length"
        type="warning"
        variant="outlined"
        density="compact"
        class="mb-3"
      >
        <div class="font-weight-bold mb-1">
          Nicht jeder Energieträger deckt alle Jahre ab.
        </div>
        <div>
          Lückenhaft: {{ incompleteSeriesLabels.join(", ") }}
        </div>
        <div class="text-caption mt-1">
          Die Reihe „Summe" addiert je Jahr, was vorliegt. Fehlt ein Träger in einem
          Jahr, springt sie — und die Regression liest den Sprung als Trend. Für die
          Summe sind die Kennzahlen dann nicht aussagekräftig; die Reihen einzelner
          Träger bleiben es.
        </div>
      </v-alert>

      <!--
        Die Fortschreibung ist abschaltbar und standardmaessig aus — siehe die
        Begruendung am `forecastOn` im Skript.
      -->
      <div class="d-flex flex-wrap align-center ga-4 mb-3">
        <v-switch
          v-model="forecastOn"
          color="primary"
          density="compact"
          hide-details
          inset
          label="Fortschreibung anzeigen"
        />
        <v-text-field
          v-if="forecastOn"
          v-model.number="forecastYears"
          label="Zeitraum"
          suffix="Jahre"
          type="number"
          min="1"
          :max="MAX_FORECAST_YEARS"
          density="compact"
          variant="outlined"
          hide-details="auto"
          style="max-width: 150px"
          :error-messages="forecastYearsError"
        />
        <v-text-field
          v-if="forecastOn"
          v-model.number="implementationYear"
          label="Maßnahmen wirksam ab"
          type="number"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          style="max-width: 200px"
          :placeholder="String(wirksamAb)"
        />
      </div>

      <v-alert
        v-if="forecastOn && forecastIsShaky"
        type="warning"
        variant="outlined"
        density="compact"
        class="mb-3"
      >
        Die Fortschreibung stützt sich auf {{ selectedModel?.n }} Messjahre
        <span v-if="selectedModel?.r2 !== null">
          bei R² = {{ fmt(selectedModel?.r2, 3) }}</span>.
        Das Prognoseband zeigt, wie wenig daraus folgt. Die einzige unabhängige
        Variable ist das Jahr — für eine belastbare Prognose nach
        DIN EN 17669 Anhang A müsste gegen relevante Variablen
        (Gradtage, Produktionsmenge) normiert werden.
      </v-alert>

      <v-alert v-if="!selectedModel" type="info" variant="tonal" density="compact">
        Nicht genügend Verbrauchsdaten für eine Regression (mindestens {{ minPoints }} Jahre nötig).
      </v-alert>

      <template v-else>
        <div class="d-flex flex-wrap ga-2 mb-3">
          <v-chip color="primary" variant="tonal">R² = {{ fmt(selectedModel.r2, 3) }}</v-chip>
          <v-chip color="primary" variant="tonal">CVRMSE = {{ fmtPercent(selectedModel.cvrmse) }}</v-chip>
          <v-chip variant="tonal">n = {{ selectedModel.n }}</v-chip>
        </div>
        <div class="equation mb-4">{{ selectedEquation }}</div>

        <div class="chart-wrapper mb-6">
          <Line :data="regressionChartData" :options="regressionOptions" />
        </div>

        <div class="text-subtitle-2 mb-1">Standardisierte Residuen</div>
        <div class="text-caption text-medium-emphasis mb-2">
          Normalverteilt, wenn ~95 % zwischen ±1,96 und ~99 % zwischen ±2,58 liegen (hier:
          {{ fmtPercent(selectedModel.residualNormality.within1_96) }} bzw.
          {{ fmtPercent(selectedModel.residualNormality.within2_58) }}).
        </div>
        <div class="chart-wrapper">
          <Line :data="residualChartData" :options="residualOptions" />
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import api from "@/plugins/axios";
import { toast } from "vue3-toastify";

// `Filler` ist neu: ohne ihn bleibt das Prognoseband ungefuellt und man
// sieht nur zwei duenne Linien.
ChartJS.register(
  LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Title, Filler,
);

const props = defineProps({
  projectId: { type: [String, Number], default: null },
  /*
   * Die Einsparung je Energieträger aus den Maßnahmen (Referenz minus
   * Geplant), in kWh/a. Die Prognose zieht sie von der fortgeschriebenen
   * Baseline ab. Kommt von der Energiebilanz-Seite, die sie ohnehin schon
   * berechnet — hier noch einmal aus den Rohdaten zu bilden hiesse, dieselbe
   * Zahl zweimal zu führen.
   */
  savingsByFuel: { type: Object, default: () => ({}) },
  /*
   * Dasselbe für die Emissionen, in Tonnen CO₂e. Kommt aus
   * `ref_co2_energy` minus `plan_co2_energy` — der Aufteilung des Servers,
   * die über dieselbe Rechnung entsteht wie die Gesamtzahlen und die
   * Gutschriften für erneuerbaren Strom bereits enthält.
   */
  co2SavingsByFuel: { type: Object, default: () => ({}) },
  /** Letztes Jahr mit gemessenem Verbrauch — Vorgabe für das Umsetzungsjahr. */
  lastBaselineYear: { type: Number, default: null },
});

const series = ref([]);
const minPoints = ref(3);
const selectedKey = ref("total");

/*
 * Die Fortschreibung ist bewusst abschaltbar und **standardmässig aus**.
 *
 * Die einzige unabhängige Variable des Modells ist das Jahr. Eine
 * Fortschreibung besagt damit nur „so wie bisher weiter" — sie ist keine
 * Prognose im Sinne von DIN EN 17669 Anhang A, wo gegen relevante Variablen
 * (Gradtage, Produktionsmenge) normiert wird. Wer sie einschaltet, soll das
 * bewusst tun und das Prognoseband sehen.
 */
/*
 * Verbrauch oder Emissionen. Beide Reihen stammen aus denselben
 * Verbrauchsdaten; je Träger ist die CO₂-Reihe nur skaliert. Bei der
 * **Summe** ändert sich die Form aber sehr wohl, weil die Träger verschieden
 * schwer wiegen — 1 kWh Netzstrom trägt mit 427 g, 1 kWh Hackschnitzel mit
 * 25 g bei. Genau dafür ist der Umschalter da.
 */
const metric = ref("consumption");
const unit = ref("kWh");
const valueLabel = ref("Verbrauch");
const incompleteFuelIds = ref([]);

const forecastOn = ref(false);
const forecastYears = ref(10);
const implementationYear = ref(null);
const MAX_FORECAST_YEARS = 20;

const keyOf = (s) => (s?.fuelId === null || s?.fuelId === undefined ? "total" : String(s.fuelId));

const seriesOptions = computed(() => series.value.map((s) => ({ label: s.label, key: keyOf(s) })));
const selectedSeries = computed(
  () => series.value.find((s) => keyOf(s) === selectedKey.value) || null,
);
const selectedModel = computed(() => selectedSeries.value?.model || null);
const selectedEquation = computed(() => selectedSeries.value?.equation || "");

async function load() {
  if (!props.projectId) return;
  try {
    const jahre = forecastOn.value ? Math.min(Number(forecastYears.value) || 0, MAX_FORECAST_YEARS) : 0;
    const { data } = await api.get(
      `/projects/${props.projectId}/regression/`,
      { params: { years: jahre, metric: metric.value } });
    series.value = data?.series || [];
    unit.value = data?.unit || "kWh";
    valueLabel.value = data?.valueLabel || "Verbrauch";
    incompleteFuelIds.value = data?.incompleteFuelIds || [];
    minPoints.value = data?.minPoints || 3;
    const total = series.value.find((s) => s.fuelId === null || s.fuelId === undefined);
    selectedKey.value = total ? "total" : series.value[0] ? keyOf(series.value[0]) : "total";
  } catch {
    toast.error("Regressionsanalyse konnte nicht geladen werden.");
  }
}

onMounted(load);
watch(() => props.projectId, load);
// Die Fortschreibung rechnet der Server — Ein-/Ausschalten und ein anderer
// Zeitraum brauchen deshalb einen neuen Abruf. Das Umsetzungsjahr nicht: es
// verschiebt nur, ab wann die Einsparung abgezogen wird.
watch([forecastOn, forecastYears, metric], load);

const numberFmt = new Intl.NumberFormat("de-DE");
const fmt = (value, decimals = 2) =>
  value === null || value === undefined
    ? "–"
    : new Intl.NumberFormat("de-DE", {
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
      }).format(value);
const fmtPercent = (value) =>
  value === null || value === undefined
    ? "–"
    : new Intl.NumberFormat("de-DE", { style: "percent", maximumFractionDigits: 1 }).format(value);

const selectedForecast = computed(() => selectedSeries.value?.forecast || null);

/**
 * Die Einsparung des gewählten Trägers, in der Einheit der gewählten Grösse.
 *
 * Bei „Summe" über alle Träger. Das ist auch der einzige Platz, an dem
 * Einsparungen an **Nutz**energie (Wärme, Kälte) auftauchen: sie haben im
 * Verbrauchskatalog keinen eigenen Träger, weil Wärme aus Erdgas oder
 * Fernwärme entsteht.
 */
const selectedSavings = computed(() => {
  const alle = (metric.value === "co2" ? props.co2SavingsByFuel : props.savingsByFuel) || {};
  if (selectedKey.value === "total") {
    return Object.values(alle).reduce((summe, wert) => summe + (Number(wert) || 0), 0);
  }
  return Number(alle[Number(selectedKey.value)]) || 0;
});

/** Ab wann die Maßnahmen wirken. Vorgabe: das Jahr nach der letzten Messung. */
const wirksamAb = computed(() => {
  const gesetzt = Number(implementationYear.value);
  if (Number.isFinite(gesetzt) && gesetzt > 0) return gesetzt;
  return (props.lastBaselineYear || new Date().getFullYear()) + 1;
});

/*
 * Ist die Anpassung gut genug, um überhaupt fortgeschrieben zu werden?
 *
 * Kein hartes Verbot — die Entscheidung trifft der Auditor. Aber bei einem
 * R² unter 0,7 oder nur drei Stützstellen gehört ein Hinweis daneben, sonst
 * liest sich die Gerade wie eine Zusage.
 */
/** Die Namen der Träger mit Lücken — für die Meldung über dem Diagramm. */
const incompleteSeriesLabels = computed(() =>
  incompleteFuelIds.value
    .map((id) => series.value.find((reihe) => reihe.fuelId === id)?.label || `Energieträger ${id}`));

const forecastYearsError = computed(() => {
  const jahre = Number(forecastYears.value);
  if (!Number.isFinite(jahre) || jahre < 1) return "Mindestens ein Jahr.";
  if (jahre > MAX_FORECAST_YEARS) return `Höchstens ${MAX_FORECAST_YEARS} Jahre.`;
  return "";
});

const forecastIsShaky = computed(() => {
  const model = selectedModel.value;
  if (!model) return false;
  return model.n < 4 || (model.r2 !== null && model.r2 < 0.7);
});

const regressionChartData = computed(() => {
  const model = selectedModel.value;
  if (!model) return { datasets: [] };
  const points = model.x.map((year, i) => ({ x: year, y: model.y[i] }));
  const line = model.x.map((year, i) => ({ x: year, y: model.predictions[i] }));

  const prognose = forecastOn.value ? selectedForecast.value : null;
  const prognoseReihen = [];
  if (prognose) {
    const jahre = prognose.years;
    // Am letzten Messpunkt ansetzen, damit die Fortschreibung an der Geraden
    // haengt und nicht daneben beginnt.
    const anschluss = { x: model.x.at(-1), y: model.predictions.at(-1) };

    if (prognose.upper && prognose.lower) {
      /*
       * Das Band als zwei Linien mit Fuellung dazwischen. Es ist das
       * **Prognoseintervall** einer Einzelbeobachtung und damit bewusst breit:
       * mit drei Messjahren ist das t-Quantil 12,7, und das soll man sehen.
       */
      prognoseReihen.push({
        label: "Prognoseband (95 %)",
        data: [anschluss, ...jahre.map((jahr, i) => ({ x: jahr, y: prognose.upper[i] }))],
        showLine: true, pointRadius: 0, borderWidth: 0,
        backgroundColor: "rgba(239, 108, 0, 0.12)",
        borderColor: "rgba(239, 108, 0, 0)",
        fill: "+1",
      });
      prognoseReihen.push({
        label: "Prognoseband unten",
        data: [anschluss, ...jahre.map((jahr, i) => ({ x: jahr, y: prognose.lower[i] }))],
        showLine: true, pointRadius: 0, borderWidth: 0,
        borderColor: "rgba(239, 108, 0, 0)",
        fill: false,
      });
    }

    prognoseReihen.push({
      label: "Fortschreibung (ohne Maßnahmen)",
      data: [anschluss, ...jahre.map((jahr, i) => ({ x: jahr, y: prognose.predictions[i] }))],
      showLine: true, pointRadius: 0, borderWidth: 2, borderDash: [6, 4],
      borderColor: "#ef6c00", backgroundColor: "#ef6c00", fill: false,
    });

    /*
     * Das Maßnahmenszenario gilt für beide Grössen. Beim Verbrauch kommt die
     * Einsparung als kWh/a von der Energiebilanz, bei den Emissionen als
     * Tonnen CO₂e aus der Aufteilung des Servers — in beiden Fällen aus der
     * Rechnung, die auch die Karten darüber speist, und nicht hier neu
     * gebildet.
     */
    const einsparung = selectedSavings.value;
    if (einsparung > 0) {
      prognoseReihen.push({
        label: "Mit Maßnahmen (Geplant)",
        data: [
          anschluss,
          ...jahre.map((jahr, i) => ({
            // Erst ab dem Umsetzungsjahr abziehen — davor laeuft der Bestand
            // unveraendert weiter.
            x: jahr,
            y: prognose.predictions[i] - (jahr >= wirksamAb.value ? einsparung : 0),
          })),
        ],
        showLine: true, pointRadius: 0, borderWidth: 2,
        borderColor: "#2e7d32", backgroundColor: "#2e7d32", fill: false,
      });
    }
  }

  return {
    datasets: [
      {
        label: "Messwerte",
        data: points,
        showLine: false,
        pointRadius: 5,
        backgroundColor: "#1565c0",
        borderColor: "#1565c0",
      },
      {
        label: "Regression",
        data: line,
        showLine: true,
        pointRadius: 0,
        borderColor: "#ef6c00",
        backgroundColor: "#ef6c00",
        borderWidth: 2,
      },
      ...prognoseReihen,
    ],
  };
});

const regressionOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  /*
   * `datalabels: false`.
   *
   * Das Plugin ist anwendungsweit registriert (die VALERI-Seite tut das am
   * gemeinsamen `Chart`). Ohne Abschalten beschriftet es hier **jeden**
   * Punkt — bei vier Messwerten plus zehn Prognosejahren mal vier Reihen
   * ergibt das einen unlesbaren Teppich. Beschriftungen will dieses Diagramm
   * nirgends.
   */
  plugins: {
    datalabels: false,
    title: { display: false },
    legend: {
      display: true,
      labels: {
        // Die untere Bandkante ist nur der Fuellpartner der oberen und
        // gehoert nicht in die Legende.
        filter: (eintrag) => eintrag.text !== "Prognoseband unten",
      },
    },
  },
  scales: {
    x: {
      type: "linear",
      title: { display: true, text: "Jahr" },
      ticks: { stepSize: 1, callback: (value) => `${value}` },
    },
    y: {
      title: { display: true, text: `${valueLabel.value} [${unit.value}]` },
      ticks: { callback: (value) => numberFmt.format(value) },
    },
  },
}));

const residualChartData = computed(() => {
  const model = selectedModel.value;
  if (!model) return { datasets: [] };
  const res = model.x.map((year, i) => ({ x: year, y: model.standardizedResiduals[i] }));
  const bound = (value) => model.x.map((year) => ({ x: year, y: value }));
  const gridLine = (label, value) => ({
    label,
    data: bound(value),
    showLine: true,
    pointRadius: 0,
    borderColor: "#9e9e9e",
    borderDash: [6, 4],
    borderWidth: 1,
  });
  return {
    datasets: [
      {
        label: "Standardisierte Residuen",
        data: res,
        showLine: false,
        pointRadius: 5,
        backgroundColor: "#2e7d32",
        borderColor: "#2e7d32",
      },
      gridLine("+1,96", 1.96),
      gridLine("−1,96", -1.96),
    ],
  };
});

const residualOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { datalabels: false, legend: { display: true } },
  scales: {
    x: {
      type: "linear",
      title: { display: true, text: "Jahr" },
      ticks: { stepSize: 1, callback: (value) => `${value}` },
    },
    y: { title: { display: true, text: "Standardisiertes Residuum" } },
  },
}));
</script>

<style scoped>
.chart-wrapper {
  position: relative;
  height: 320px;
}

.equation {
  font-family: "Cambria Math", Georgia, serif;
  font-size: 1.05rem;
}
</style>
